import { titleize, truncate } from '../../../utils/helpers';

import { PLOTLY_DEFAULT_YAXIS_NTICKS } from '../constants';

const getXAxis = (data, graphLayout, isInModal, xAxisRange = [], maxXAxisTicks = 50) => {
  const maxXAxisTickChars = 20;

  let xAxisTickValues = data[0]?.x || [];
  // truncate x-axis values to `maxXAxisTickChars`` characters
  let xAxisTickTexts = xAxisTickValues.map(value => truncate(value, maxXAxisTickChars));
  // if there are more than `maxXAxisTicks`` x-axis values and no xAxisRange is provided, we only show every other value
  if (xAxisTickTexts.length > maxXAxisTicks && xAxisRange.length === 0 && maxXAxisTicks > 0) {
    const step = Math.ceil(xAxisTickTexts.length / maxXAxisTicks);
    xAxisTickValues = xAxisTickValues.filter((_, index) => index % step === 0);
    xAxisTickTexts = xAxisTickTexts.filter((_, index) => index % step === 0);
  }

  // if x-axis range is provided, we only show the x-axis values within the range
  if (xAxisRange.length > 0) {
    const [start, end] = xAxisRange;
    const startIndex = Math.max(Math.floor(start), 0); // start index should not be negative
    const endIndex = Math.max(Math.ceil(end), 0); // end index should not be negative
    xAxisTickValues = xAxisTickValues.slice(startIndex, endIndex + 1);
    xAxisTickTexts = xAxisTickTexts.slice(startIndex, endIndex + 1);
  }

  return {
    xaxis: {
      ...graphLayout.xaxis,
      range: xAxisRange,
      // if there are more than 80 x-axis values, we need to reduce the font size to avoid overlapping
      ...(xAxisTickValues.length > 80 && {
        tickfont: { size: 9 },
      }),
      // if in modal or x-axis not categorical, let Plotly handle the x-axis ticks
      ...(!isInModal &&
        graphLayout.xaxis.type === 'category' && {
          tickvals: xAxisTickValues,
          ticktext: xAxisTickTexts,
        }),
    },
  };
};

const getYAxis = (data, graphLayout, yAxisRange = []) => {
  const maxY = data.reduce((sum, d) => sum + Math.max(...d.y), 0);

  return {
    yaxis: {
      ...graphLayout.yaxis,
      // if the maxY values is lower than 5 (plotly default), reduce the number of nticks to the maxY value,
      // but at least 2 (to avoid showing just 0)
      nticks: maxY < PLOTLY_DEFAULT_YAXIS_NTICKS ? Math.max(maxY, 2) : null,
      range: yAxisRange,
    },
  };
};

export const getAdjustedLayout = ({
  data,
  graphLayout,
  isInModal,
  showLegend,
  xAxisRange = [],
  yAxisRange = [],
  maxXAxisTicks = 50,
}) => {
  const uniqueXValues = new Set(data[0]?.x);
  if (uniqueXValues.size === 1) {
    // if there is only one x-axis value, set x-axis type to category to avoid showing time on x-axis ticks
    graphLayout.xaxis.type = 'category';
  }

  const layout = {
    showlegend: showLegend,
    ...graphLayout,
    ...getYAxis(data, graphLayout, yAxisRange),
    // if not in modal and x-axis is categorical, we need to truncate the x-axis values
    ...getXAxis(data, graphLayout, isInModal, xAxisRange, maxXAxisTicks),
    // if is in modal, place legend outside of the graph
    ...(isInModal && {
      legend: {
        x: 1.05,
        y: 0.5,
        traceorder: 'normal',
      },
    }),
  };

  return layout;
};

export const getGraphLayout = ({
  showLegend,
  yAxisTitle,
  altYAxisTitle,
  xAxisType = 'category',
}) => ({
  hoverlabel: { align: 'left' },
  annotations: [
    {
      xref: 'paper',
      yref: 'paper',
      x: 1,
      xanchor: 'right',
      y: 1,
      yanchor: 'bottom',
      text: 'Source: Welligence Energy Analytics',
      showarrow: false,
      font: {
        color: 'lightgrey',
        size: 10,
      },
    },
  ],
  margin: {
    l: yAxisTitle ? 100 : 50,
    r: altYAxisTitle ? 100 : 15,
    b: 60,
    t: 30,
  },
  xaxis: {
    type: xAxisType,
    tickangle: 'auto',
    automargin: true,
  },
  barmode: 'stack',
  legend: {
    x: 1,
    xanchor: 'right',
    y: 1,
    bgcolor: 'rgba(236, 240, 245, 0.8)',
    traceorder: 'normal',
  },
  showLegend,
  yaxis: {
    ...(yAxisTitle && {
      title: { text: yAxisTitle, standoff: 15 }, // increased standoff for y-axis title
    }),
    standoff: 15, // increased standoff for y-axis ticks
    automargin: true,
    tickformat: ',d',
    rangemode: 'tozero',
    zeroline: true,
  },
  ...(altYAxisTitle && {
    yaxis2: {
      title: {
        text: altYAxisTitle,
        standoff: 15, // increased standoff for alt y-axis title
      },
      overlaying: 'y',
      side: 'right',
      tickformat: ',d',
      rangemode: 'tozero',
      standoff: 15, // increased standoff for yaxis2 ticks
    },
  }),
});

export const getChartOptions = (data = []) => {
  return data.map(d => ({
    value: d,
    label: titleize(d),
  }));
};

const MIN_SCATTER_MARKER_SIZE = 6;
const MAX_SCATTER_MARKER_SIZE = 14;

export const getScatterMarkerSize = data => {
  const dataLength = data?.length || 0;
  // scale marker size based on number of data points, but never let it go lower than min, or larger than max
  const markerSize = Math.ceil(MAX_SCATTER_MARKER_SIZE / (dataLength / 10));
  return Math.min(Math.max(MIN_SCATTER_MARKER_SIZE, markerSize), MAX_SCATTER_MARKER_SIZE);
};
